import type {
  AiOperationsRun,
  ApprovalProposalAction,
  ApprovalProposalListItem,
  RiskLevel,
} from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowUp,
  IconChevronRight,
  IconCircleCheck,
  IconMinus,
  IconPlayerPause,
  IconPlus,
  IconTrendingUp,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import type * as React from "react";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { cn } from "@/lib/utils";
import { DashboardCard, DashboardCardHeader, DashboardCardLink } from "./dashboard-card";
import { formatTimeLeft } from "./overview-format";

export type AttentionKind = "failed" | "bidPlan" | "harvest" | "negative" | "keywordStatus";

/** One queue row; every row opens the AI run that produced it. */
export interface AttentionItem {
  id: string;
  kind: AttentionKind;
  runId: string;
  campaignName: string;
  risk: RiskLevel | null;
  expiresAt: string | null;
  /** failed: failed / total actions of the run */
  failed?: { failed: number; total: number };
  /** proposal actions still waiting for review */
  actions?: ApprovalProposalAction[];
}

const BID_ACTIONS = new Set(["increase_bid", "decrease_bid", "keep_bid"]);

function proposalKind(actions: ApprovalProposalAction[]): AttentionKind {
  if (actions.some((action) => BID_ACTIONS.has(action.actionType))) return "bidPlan";
  if (actions.some((action) => action.actionType === "suggest_harvest_keyword")) return "harvest";
  if (actions.some((action) => action.actionType === "suggest_negative_keyword")) return "negative";
  return "keywordStatus";
}

/**
 * Failed runs first (newest first), then pending proposals by review deadline.
 * Bid budget / placement changes are not part of the approval queue yet, so the
 * queue only shows the kinds the API can produce.
 */
export function buildAttentionItems(
  proposals: readonly ApprovalProposalListItem[],
  failedRuns: readonly AiOperationsRun[],
  limit = 5,
): AttentionItem[] {
  const failedItems: AttentionItem[] = failedRuns.map((run) => ({
    id: `run:${run.id}`,
    kind: "failed",
    runId: run.id,
    campaignName: run.campaignName ?? run.managedGroupName ?? "—",
    risk: null,
    expiresAt: null,
    failed: { failed: run.failed, total: Math.max(run.candidateActions, run.failed) },
  }));
  const proposalItems: AttentionItem[] = proposals
    .map((proposal) => {
      const pending = proposal.actions.filter((action) => action.reviewStatus === "pending");
      const actions = pending.length > 0 ? pending : proposal.actions;
      return {
        id: `proposal:${proposal.id}`,
        kind: proposalKind(actions),
        runId: proposal.runId,
        campaignName: proposal.campaignName,
        risk: proposal.highestRisk,
        expiresAt: proposal.expiresAt,
        actions,
      } satisfies AttentionItem;
    })
    .sort(
      (a, b) =>
        (a.expiresAt ? Date.parse(a.expiresAt) : Number.POSITIVE_INFINITY) -
        (b.expiresAt ? Date.parse(b.expiresAt) : Number.POSITIVE_INFINITY),
    );
  return [...failedItems, ...proposalItems].slice(0, limit);
}

const KIND_ICONS = {
  failed: IconAlertTriangle,
  bidPlan: IconTrendingUp,
  harvest: IconPlus,
  negative: IconMinus,
  keywordStatus: IconPlayerPause,
} as const satisfies Record<AttentionKind, React.ComponentType<{ className?: string }>>;

function AttentionSummary({ item, t }: { item: AttentionItem; t: TFunction<"dashboard"> }) {
  if (item.failed) {
    return t("overview.attention.summary.failed", {
      failed: item.failed.failed,
      count: item.failed.total,
    });
  }
  const actions = item.actions ?? [];
  const [first] = actions;
  if (item.kind === "bidPlan") {
    const bids = actions.filter((action) => BID_ACTIONS.has(action.actionType));
    const up = bids.filter((action) => action.actionType === "increase_bid").length;
    const down = bids.filter((action) => action.actionType === "decrease_bid").length;
    return (
      <>
        {t("overview.attention.summary.bidPlan", { count: bids.length })}
        {up + down > 0 ? (
          <span className="inline-flex items-center">
            <span aria-hidden className="mx-1.5">
              ·
            </span>
            {up > 0 ? (
              <span className="inline-flex items-center">
                {up}
                <IconArrowUp
                  aria-label={t("overview.attention.summary.up")}
                  className="size-3.5"
                  strokeWidth={1.75}
                />
              </span>
            ) : null}
            {down > 0 ? (
              <span className={cn("inline-flex items-center", up > 0 && "ml-1")}>
                {down}
                <IconArrowDown
                  aria-label={t("overview.attention.summary.down")}
                  className="size-3.5"
                  strokeWidth={1.75}
                />
              </span>
            ) : null}
          </span>
        ) : null}
      </>
    );
  }
  if (actions.length === 1 && first) {
    return t(`overview.attention.summary.${item.kind}One`, { keyword: first.targetLabel });
  }
  return t(`overview.attention.summary.${item.kind}`, { count: actions.length });
}

const RISK_BADGE: Partial<Record<RiskLevel, string>> = {
  high: "bg-destructive-background text-destructive-foreground",
  critical: "bg-destructive-background text-destructive-foreground",
};

function AttentionRow({ item, now }: { item: AttentionItem; now: Date }) {
  const { t } = useTranslation("dashboard");
  const Icon = KIND_ICONS[item.kind];
  const failed = item.kind === "failed";
  const riskClass = item.risk ? RISK_BADGE[item.risk] : undefined;
  const timeLeft = item.expiresAt ? formatTimeLeft(item.expiresAt, now, t) : null;

  return (
    <li className="border-b border-line last:border-b-0">
      <Link
        to="/ai-operations/$runId"
        params={{ runId: item.runId }}
        className="flex h-12 items-center gap-4 pr-3 pl-5 outline-none transition-colors hover:bg-table-row-hover focus-visible:bg-table-row-hover focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset"
      >
        <span
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-sm",
            failed
              ? "bg-destructive-background text-destructive-foreground"
              : "bg-subtle text-text-secondary",
          )}
        >
          <Icon aria-hidden className="size-4" strokeWidth={1.75} />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span
            className={cn(
              "shrink-0 text-xs font-medium",
              failed ? "text-destructive-foreground" : "text-muted-foreground",
            )}
          >
            {t(`overview.attention.kind.${item.kind}`)}
          </span>
          <span className="truncate text-sm font-medium text-ink">{item.campaignName}</span>
        </span>
        <span className="hidden w-[280px] shrink-0 truncate text-sm tabular-nums text-text-secondary @min-[720px]:block @max-[880px]:w-[200px]">
          <AttentionSummary item={item} t={t} />
        </span>
        <span className="hidden w-[92px] shrink-0 @min-[560px]:flex">
          {riskClass && item.risk ? (
            <span
              className={`inline-flex h-5 items-center rounded-xs px-1.5 text-badge font-medium ${riskClass}`}
            >
              {t(`overview.attention.risk.${item.risk}`)}
            </span>
          ) : null}
        </span>
        <span
          className={cn(
            "w-[84px] shrink-0 truncate text-xs font-medium tabular-nums",
            timeLeft?.urgent ? "text-warning-foreground" : "text-text-secondary",
          )}
        >
          {timeLeft?.label}
        </span>
        <span aria-hidden className="flex size-7 shrink-0 items-center justify-center">
          <IconChevronRight className="size-4 text-muted-foreground" strokeWidth={1.75} />
        </span>
      </Link>
    </li>
  );
}

export interface AttentionCardProps {
  items: readonly AttentionItem[];
  now: Date;
  isLoading?: boolean;
  isError?: boolean;
  incomplete?: boolean;
  onRetry?: () => void;
  className?: string;
}

/** Paper "Card · Needs your attention": failed runs and pending proposals, each row opens the run. */
export function AttentionCard({
  items,
  now,
  isLoading = false,
  isError = false,
  incomplete = false,
  onRetry,
  className,
}: AttentionCardProps) {
  const { t } = useTranslation("dashboard");

  return (
    <DashboardCard aria-labelledby="overview-attention-title" className={className}>
      <DashboardCardHeader
        titleId="overview-attention-title"
        title={t("overview.attention.title")}
        className="min-h-14 border-b border-line"
        action={
          <DashboardCardLink to="/ai-operations" search={DEFAULT_APPROVAL_SEARCH}>
            {t("overview.attention.operations")}
          </DashboardCardLink>
        }
      />
      {isLoading ? (
        <div className="flex flex-col">
          {[0, 1, 2].map((row) => (
            <div
              key={row}
              className="flex h-12 items-center gap-4 border-b border-line px-5 last:border-b-0"
            >
              <Skeleton className="size-7 rounded-sm" />
              <Skeleton className="h-4 w-56" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="px-5 py-3">
          <ErrorState
            variant="inline"
            message={t("overview.attention.loadFailed")}
            onRetry={onRetry}
          />
        </div>
      ) : incomplete ? (
        <p role="status" className="px-5 py-3 text-sm text-muted-foreground">
          {t("overview.aiStatus.incomplete")}
        </p>
      ) : items.length === 0 ? (
        <p className="flex h-12 items-center gap-2 px-5 text-sm text-muted-foreground">
          <IconCircleCheck
            aria-hidden
            className="size-4 text-success-foreground"
            strokeWidth={1.75}
          />
          {t("overview.attention.empty")}
        </p>
      ) : (
        <ul>
          {items.map((item) => (
            <AttentionRow key={item.id} item={item} now={now} />
          ))}
        </ul>
      )}
    </DashboardCard>
  );
}
